import { IncorporationRequestDataType } from '@commenda/types';
import { LegacyDocumentType } from '@commenda/prisma';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { Country, State } from 'country-state-city';
import { DocumentSummaryField, SummaryFieldKS } from '../common';
import { getDirectorName } from '../Director/common';

const UKFounderField = ({
  director,
  data,
}: {
  director: DirectorBase;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
}) => {
  const directorVsEquityMap: Record<
    number,
    { sharePercentage: number; name: string; id: number }
  > = {};

  data?.directors?.forEach((dir) => {
    const details = dir.corporationHasDirector.find(
      (directorWithCorporation) =>
        directorWithCorporation.corporationId === data.corporationId
    )?.additionalDirectorInformation;

    directorVsEquityMap[dir.id] = {
      name: getDirectorName(dir),
      sharePercentage: details?.sharePercentage,
      id: dir.id,
    };
  });

  const openFile = async (fileId: number) => {
    const res = await getSignedUrlByFileId(fileId, null);
    window.open(res);
  };

  const phoneNumber = director?.number;
  const email = director?.email;
  const currentAddress = director?.operatingAddress;
  const sharePercentage = directorVsEquityMap[director.id]?.sharePercentage;
  const nationality = director?.personalDetails?.nationality;
  const countryOfResidence = director?.personalDetails?.residence;
  const hasSignificantControl = director?.corporationHasDirector?.find(
    (chd) =>
      chd.corporationId === data?.corporationId &&
      director?.id === chd.directorId
  )?.additionalDirectorInformation?.hasSignificantControl;

  const hasUsedDifferentName = director?.corporationHasDirector?.find(
    (chd) =>
      chd.corporationId === data?.corporationId &&
      director?.id === chd.directorId
  )?.incorporationInfo?.hasUsedDifferentName;

  const jobTitle = director?.corporationHasDirector?.find(
    (chd) =>
      chd.corporationId === data?.corporationId &&
      director?.id === chd.directorId
  )?.incorporationInfo?.jobTitle;

  const isDigitallyAuthorisedToFileOnline =
    director?.corporationHasDirector?.find(
      (chd) =>
        chd.corporationId === data?.corporationId &&
        director?.id === chd.directorId
    )?.additionalDirectorInformation?.isDigitallyAuthorisedToFileOnline;

  const passportFiles =
    director?.directorFiles?.filter(
      (file) => file.documentType === LegacyDocumentType.Passport
    ) ?? [];

  const passportFile =
    passportFiles.length > 0 ? passportFiles[passportFiles.length - 1] : null;

  const governmentIdFile = director?.directorFiles?.find(
    (file) => file.documentType === LegacyDocumentType.GovernmentId
  );

  return (
    <div className="p-6 flex flex-col gap-2 bg-bg-disabled-subtle">
      <SummaryFieldKS 
        heading="First Name" 
        value={director?.directorFirstName || '-'} 
      />
      <SummaryFieldKS 
        heading="Last Name" 
        value={director?.directorLastName || '-'} 
      />
      
      {sharePercentage && sharePercentage !== 0 ? (
        <SummaryFieldKS
          heading="Capital contributions"
          value={`${sharePercentage}%`}
        />
      ) : null}

      {phoneNumber && <SummaryFieldKS heading="Phone" value={phoneNumber} />}
      {email && <SummaryFieldKS heading="Email" value={email} />}
      {nationality ? (
        <SummaryFieldKS
          heading="Nationality"
          value={Country.getCountryByCode(nationality)?.name}
        />
      ) : null}
      {countryOfResidence ? (
        <SummaryFieldKS
          heading="Country of residence"
          value={Country.getCountryByCode(countryOfResidence)?.name}
        />
      ) : null}
      <SummaryFieldKS heading="Date of birth" value={director?.dateOfBirth} />
      <SummaryFieldKS
        heading="Person with significant control"
        value={hasSignificantControl ? 'Yes' : 'No'}
      />
      {hasUsedDifferentName !== null && hasUsedDifferentName !== undefined && (
        <SummaryFieldKS
          heading="Used different name for business purposes in last 20 years"
          value={hasUsedDifferentName ? 'Yes' : 'No'}
        />
      )}
      {jobTitle && <SummaryFieldKS heading="Job title" value={jobTitle} />}
      {isDigitallyAuthorisedToFileOnline !== null &&
        isDigitallyAuthorisedToFileOnline !== undefined && (
          <SummaryFieldKS
            heading="Digitally authorised to file online"
            value={isDigitallyAuthorisedToFileOnline ? 'Yes' : 'No'}
          />
        )}
      {currentAddress ? (
        <SummaryFieldKS
          heading="Current address"
          value={[
            `${currentAddress?.addressLine1}, ${currentAddress?.addressLine2}`,
            `${currentAddress?.city} - ${currentAddress?.postalCode}`,
            `${
              State.getStateByCodeAndCountry(
                currentAddress?.state,
                currentAddress?.country
              )?.name
            }, ${Country.getCountryByCode(currentAddress?.country)?.name}`,
          ]}
        />
      ) : null}

      {passportFile?.fileUrl ? (
        <div className="mt-2">
          <DocumentSummaryField
            onClick={() => openFile(passportFile?.id)}
            title="Passport"
            className="grid grid-cols-2 gap-8"
          />
        </div>
      ) : null}
      {governmentIdFile?.fileUrl ? (
        <div className="mt-2">
          <DocumentSummaryField
            onClick={() => openFile(governmentIdFile?.id)}
            title="Government ID"
            className="grid grid-cols-2 gap-8"
          />
        </div>
      ) : null}
    </div>
  );
};

export default UKFounderField;
